import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getIntlLocaleTag, mapBackendLanguageToApp } from '@src/shared/lib';
import AsyncAutocompleteInput from '@src/shared/ui/inputs/AsyncAutocompleteInput';

type GoogleAddressComponent = {
  longText?: string;
  shortText?: string;
  types: string[];
};

type GooglePlace = {
  addressComponents?: GoogleAddressComponent[];
  fetchFields: (request: { fields: string[] }) => Promise<{ place: GooglePlace }>;
};

type GooglePlaceOptions = {
  id: string;
  requestedLanguage?: string;
  requestedRegion?: string;
};

type GooglePlacePrediction = {
  placeId: string;
  text: {
    text: string;
  };
  mainText?: {
    text: string;
  };
  secondaryText?: {
    text: string;
  };
  toPlace: () => GooglePlace;
};

type GoogleAutocompleteSuggestion = {
  placePrediction?: GooglePlacePrediction;
};

type GooglePlacesLibrary = {
  Place: new (options: GooglePlaceOptions) => GooglePlace;
  AutocompleteSuggestion: {
    fetchAutocompleteSuggestions: (request: {
      input: string;
      language?: string;
      includedRegionCodes?: string[];
    }) => Promise<{
      suggestions: GoogleAutocompleteSuggestion[];
    }>;
  };
};

type GoogleMapsApi = {
  maps: {
    importLibrary: (library: 'places') => Promise<GooglePlacesLibrary>;
    places?: GooglePlacesLibrary;
  };
};

type WindowWithGoogleMaps = Window & {
  google?: GoogleMapsApi;
};

type AddressPrediction = {
  description: string;
  placeId: string;
  structured_formatting?: {
    main_text?: string;
    secondary_text?: string;
  };
  placePrediction: GooglePlacePrediction;
};

export type GoogleAddress = {
  addressLine1: string;
  addressLine2: string;
  postcode: string;
  city: string;
};

type GoogleAddressAutocompleteProps = {
  countryCode?: string | null;
  label: string;
  placeholder: string;
  disabled?: boolean;
  onAddressSelect: (address: GoogleAddress) => void;
};

const GOOGLE_MAPS_SCRIPT_ID = 'google-maps-places-script';
const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
const GOOGLE_MAPS_LOAD_RETRY_DELAY_MS = 100;
const GOOGLE_MAPS_LOAD_MAX_RETRIES = 50;

export const isGoogleMapsAvailable = !!GOOGLE_MAPS_API_KEY;

const googleMapsScriptPromises = new Map<string, Promise<GooglePlacesLibrary>>();

function getGoogleLanguage(resolvedLanguage: string | undefined) {
  const appLanguage = mapBackendLanguageToApp(resolvedLanguage);
  return getIntlLocaleTag(appLanguage);
}

function getGoogleMapsApi() {
  return (window as WindowWithGoogleMaps).google;
}

function waitForGoogleMapsApi(attempt = 0): Promise<GoogleMapsApi> {
  const googleApi = getGoogleMapsApi();
  if (googleApi?.maps.importLibrary) {
    return Promise.resolve(googleApi);
  }

  if (attempt >= GOOGLE_MAPS_LOAD_MAX_RETRIES) {
    return Promise.reject(new Error('Google Maps API is unavailable.'));
  }

  return new Promise((resolve, reject) => {
    window.setTimeout(() => {
      waitForGoogleMapsApi(attempt + 1).then(resolve).catch(reject);
    }, GOOGLE_MAPS_LOAD_RETRY_DELAY_MS);
  });
}

function loadGooglePlacesApi(language: string) {
  const existingGoogle = getGoogleMapsApi();
  if (existingGoogle?.maps.places?.AutocompleteSuggestion) {
    return Promise.resolve(existingGoogle.maps.places);
  }
  if (existingGoogle?.maps.importLibrary) {
    return existingGoogle.maps.importLibrary('places');
  }

  if (!GOOGLE_MAPS_API_KEY) {
    return Promise.reject(new Error('Google Maps API key is not configured.'));
  }

  const existingPromise = googleMapsScriptPromises.get(language);
  if (existingPromise) {
    return existingPromise;
  }

  const scriptPromise = new Promise<GooglePlacesLibrary>((resolve, reject) => {
    const existingScript = document.getElementById(GOOGLE_MAPS_SCRIPT_ID) as HTMLScriptElement | null;

    const importPlacesLibrary = async () => {
      try {
        const googleApi = await waitForGoogleMapsApi();
        resolve(await googleApi.maps.importLibrary('places'));
      } catch {
        reject(new Error('Google Places API is unavailable.'));
      }
    };

    if (existingScript) {
      if (getGoogleMapsApi()?.maps.importLibrary) {
        void importPlacesLibrary();
        return;
      }

      existingScript.addEventListener('load', () => void importPlacesLibrary());
      existingScript.addEventListener('error', () => reject(new Error('Failed to load Google Places API.')));
      return;
    }

    const script = document.createElement('script');
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(GOOGLE_MAPS_API_KEY)}&libraries=places&v=weekly&loading=async&language=${encodeURIComponent(language)}`;
    script.async = true;
    script.defer = true;
    script.onload = () => void importPlacesLibrary();
    script.onerror = () => reject(new Error('Failed to load Google Places API.'));
    document.head.appendChild(script);
  });

  googleMapsScriptPromises.set(language, scriptPromise);
  scriptPromise.catch(() => {
    googleMapsScriptPromises.delete(language);
  });
  return scriptPromise;
}

function findAddressComponent(components: GoogleAddressComponent[], type: string, name: 'longText' | 'shortText' = 'longText') {
  return components.find((component) => component.types.includes(type))?.[name] ?? '';
}

function parseGoogleAddress(place: GooglePlace): GoogleAddress {
  const components = place.addressComponents ?? [];
  const route = findAddressComponent(components, 'route');
  const streetNumber = findAddressComponent(components, 'street_number');
  const subpremise = findAddressComponent(components, 'subpremise');
  const floor = findAddressComponent(components, 'floor');
  const postalCode = findAddressComponent(components, 'postal_code');
  const postalCodeSuffix = findAddressComponent(components, 'postal_code_suffix');
  const city = findAddressComponent(components, 'locality')
    || findAddressComponent(components, 'postal_town')
    || findAddressComponent(components, 'administrative_area_level_3')
    || findAddressComponent(components, 'administrative_area_level_2');

  return {
    addressLine1: [route, streetNumber].filter(Boolean).join(' '),
    addressLine2: [subpremise, floor].filter(Boolean).join(', '),
    postcode: [postalCode, postalCodeSuffix].filter(Boolean).join('-'),
    city,
  };
}

function GoogleAddressAutocomplete({
  countryCode,
  label,
  placeholder,
  disabled = false,
  onAddressSelect,
}: GoogleAddressAutocompleteProps) {
  const { i18n } = useTranslation();
  const language = useMemo(() => getGoogleLanguage(i18n.resolvedLanguage), [i18n.resolvedLanguage]);
  const normalizedCountryCode = countryCode ? countryCode.toUpperCase() : '';
  const [inputValue, setInputValue] = useState('');
  const [value, setValue] = useState<AddressPrediction | null>(null);
  const [options, setOptions] = useState<AddressPrediction[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [placesLibrary, setPlacesLibrary] = useState<GooglePlacesLibrary | null>(null);

  useEffect(() => {
    let isMounted = true;

    setLoadFailed(false);
    setPlacesLibrary(null);

    if (!normalizedCountryCode) {
      return () => {
        isMounted = false;
      };
    }

    loadGooglePlacesApi(language)
      .then((placesLibrary) => {
        if (!isMounted) {
          return;
        }

        setPlacesLibrary(placesLibrary);
      })
      .catch(() => {
        if (isMounted) {
          setLoadFailed(true);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [language, normalizedCountryCode]);

  useEffect(() => {
    if (!normalizedCountryCode || inputValue.trim().length < 3 || !placesLibrary) {
      setOptions([]);
      setLoading(false);
      return;
    }

    let isActive = true;
    setLoading(true);

    const timeoutId = window.setTimeout(() => {
      placesLibrary.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: inputValue,
        language,
        includedRegionCodes: [normalizedCountryCode],
      })
        .then(({ suggestions }) => {
          if (!isActive) {
            return;
          }

          const predictions = suggestions
            .map((suggestion) => suggestion.placePrediction)
            .filter((prediction): prediction is GooglePlacePrediction => Boolean(prediction))
            .map((prediction) => ({
              description: prediction.text.text,
              placeId: prediction.placeId,
              structured_formatting: {
                main_text: prediction.mainText?.text,
                secondary_text: prediction.secondaryText?.text,
              },
              placePrediction: prediction,
            }));

          setLoading(false);
          setOptions(predictions);
        })
        .catch(() => {
          if (isActive) {
            setLoading(false);
            setOptions([]);
          }
        });
    }, 300);

    return () => {
      isActive = false;
      window.clearTimeout(timeoutId);
    };
  }, [inputValue, language, normalizedCountryCode, placesLibrary]);

  const handleChange = useCallback((selectedOption: AddressPrediction | null) => {
    setValue(selectedOption);

    if (!selectedOption) {
      return;
    }

    setLoading(true);
    const place = placesLibrary?.Place
      ? new placesLibrary.Place({
        id: selectedOption.placeId,
        requestedLanguage: language,
      })
      : selectedOption.placePrediction.toPlace();
    place.fetchFields({ fields: ['addressComponents'] })
      .then(({ place: fetchedPlace }) => {
        setLoading(false);
        onAddressSelect(parseGoogleAddress(fetchedPlace));
      })
      .catch(() => {
        setLoading(false);
      });
  }, [language, onAddressSelect, placesLibrary]);

  return (
    <AsyncAutocompleteInput<AddressPrediction>
      value={value}
      inputValue={inputValue}
      options={options}
      loading={loading}
      disabled={disabled || !normalizedCountryCode || loadFailed}
      label={label}
      placeholder={placeholder}
      helperText={loadFailed ? 'Google Places is unavailable.' : 'Powered by Google'}
      valuePropName="placeId"
      filterOptions={(searchOptions) => searchOptions}
      getOptionLabel={(option) => option.description}
      isOptionEqualToValue={(option, selectedValue) => option.placeId === selectedValue.placeId}
      onChange={handleChange}
      onInputChange={(_, nextInputValue) => setInputValue(nextInputValue)}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return (
          <li key={key} {...optionProps}>
            <div>
              <div>{option.structured_formatting?.main_text ?? option.description}</div>
              {option.structured_formatting?.secondary_text ? (
                <div>{option.structured_formatting.secondary_text}</div>
              ) : null}
            </div>
          </li>
        );
      }}
      fullWidth
    />
  );
}

export default GoogleAddressAutocomplete;
